import axios from 'axios';
import { onApiFailure, onApiSuccess } from 'src/utils/connectivityDetector';

const instance = axios.create({
  baseURL: `${process.env.PUBLIC_URL || ''}/api`
});

// Session-expiry handling. The authenticated app shell registers a handler that
// clears client auth state, shows a message and redirects to the login page.
// When no handler is registered (e.g. public questionnaire pages) we do nothing
// and let the rejection propagate.
let unauthorizedHandler = null;
let lastUnauthorizedAt = 0;

export function setUnauthorizedHandler(fn) {
  unauthorizedHandler = fn;
}

// Login/logout-flow endpoints: a 401 here means bad credentials / an already-
// gone session, not "your active session just expired" — so don't redirect.
const AUTH_FLOW_PATHS = ['/login', '/token', '/signout', '/sso'];
const isAuthFlowRequest = (url = '') =>
  AUTH_FLOW_PATHS.some((p) => String(url).includes(p));

function handleUnauthorized() {
  // Debounce: a burst of parallel requests can all 401 at once (polls,
  // heartbeats) — only act on the first.
  const ts = Date.now();
  if (ts - lastUnauthorizedAt < 3000) return;
  lastUnauthorizedAt = ts;
  if (typeof unauthorizedHandler === 'function') unauthorizedHandler();
}

instance.interceptors.response.use(
  (response) => {
    if (response.headers['reload-required']) window.location.reload();
    onApiSuccess();
    return response;
  },
  (err) => {
    const status = err?.response?.status;

    // Hand off to the logout/redirect handler ONLY for a genuine session
    // expiry, which the auth middleware marks with { sessionExpired: true }.
    // An authenticated-but-forbidden response (403, or a stray/legacy 401 from
    // a permission gate) must NOT log the user out — those surface as a normal
    // rejected request. Auth-flow endpoints are skipped too (bad-credentials
    // 401s there are not "your active session just expired").
    const sessionExpired = err?.response?.data?.sessionExpired === true;
    if (
      status === 401 &&
      sessionExpired &&
      !isAuthFlowRequest(err?.config?.url)
    ) {
      handleUnauthorized();
      return Promise.reject(err);
    }

    // Role/permission change can be signalled via a header on the response.
    if (err?.response?.headers?.['reload-required']) window.location.reload();

    // Count network errors and 5xx as connectivity failures; 4xx is an
    // app-level error and shouldn't trip the offline banner.
    if (!status || status >= 500) {
      onApiFailure();
    } else {
      onApiSuccess();
    }
    return Promise.reject(err);
  }
);

export default instance;
